// @owners { team: patients-team }
import { getNextAvailableDateID } from '~shared/features/next-available-date/helpers/getNextAvailableDateID';
import { type NextAvailableDate, type NextAvailableDateResponse } from '~shared/types/clients';

/** Maps a single API response to the client-side {@link NextAvailableDate} shape. Mirrors the Redux reducer mapping. */
export const mapNextAvailableDateResponseToDate = (response: NextAvailableDateResponse): NextAvailableDate => ({
  earliest: response.next_available_date,
  lastAvailableDate: response.last_available_date,
  excluded: response.excluded_dates,
  computedAt: response.computed_at,
  nextAutoRefill: response.next_auto_refill_date,
  deliveryMethod: response.delivery_method,
});

/**
 * Maps API responses to a `Record<string, NextAvailableDate>` keyed by {@link getNextAvailableDateID}.
 * Compatible with the Redux `nextAvailableDates.dates` shape — use when merging with selectors that read by
 * compound address+prescription key.
 */
export const mapNextAvailableDateResponsesToDates = (
  responses: NextAvailableDateResponse[],
): Record<string, NextAvailableDate> => {
  return responses.reduce<Record<string, NextAvailableDate>>((acc, response) => {
    const key = getNextAvailableDateID({
      addressID: response.address.id,
      facilityID: response.facility_id,
      prescriptionIDs: response.prescription_ids,
    });
    return { ...acc, [key]: mapNextAvailableDateResponseToDate(response) };
  }, {});
};

/**
 * Maps API responses to a `Record<number, NextAvailableDate>` keyed by each prescription ID.
 * When a response covers multiple prescriptions (bundled), every prescription ID maps to the same date object.
 * Use when consumers need a direct prescription-ID lookup without knowing the compound address+prescription key.
 *
 * **Duplicate prescription IDs:** If the same ID appears in more than one response, **later** items in `responses`
 * overwrite earlier ones (last-write-wins, matching `reduce` iteration order). Order the array so the intended
 * winning scope is last.
 */
export const mapNextAvailableDateResponsesByPrescriptionId = (
  responses: NextAvailableDateResponse[],
): Record<number, NextAvailableDate> => {
  return responses.reduce<Record<number, NextAvailableDate>>((acc, response) => {
    const date = mapNextAvailableDateResponseToDate(response);
    // Last-write-wins per prescription ID (see JSDoc above).
    response.prescription_ids.forEach((id) => {
      acc[id] = date;
    });
    return acc;
  }, {});
};
